<!--
+------------------------------------------------------------------+
| Author: Jeffrey Thümann                                          |
| Component: AncestorTreePersonCards                               |
| Section: views/trees                                             |
| Purpose: Ancestor tree route rendered as linked person cards.    |
+------------------------------------------------------------------+
-->
<template>
    <div class="ancestor-person-cards-view">
        <div v-if="loading" class="ancestor-person-cards-view__status">{{ loadingLabel }}</div>
        <div v-else-if="error" class="ancestor-person-cards-view__status ancestor-person-cards-view__status--error">
            {{ error }}
        </div>
        <div v-else-if="rootPerson" ref="viewport" class="ancestor-person-cards-view__canvas" @wheel="onWheel"
            @mousedown="startPan" @mousemove="onPan" @mouseup="stopPan" @mouseleave="stopPan" @touchstart="onTouchStart"
            @touchmove="onTouchMove" @touchend="onTouchEnd" @touchcancel="onTouchEnd">
            <div class="ancestor-person-cards-view__hint">{{ $t('treeViews.zoomHint') }}</div>

            <div class="ancestor-person-cards-view__controls" @mousedown.stop @click.stop>
                <button class="ancestor-person-cards-view__control-btn" @click="zoomOut"
                    :aria-label="$t('treeViews.zoomOut')">-</button>
                <button class="ancestor-person-cards-view__control-btn" @click="zoomIn"
                    :aria-label="$t('treeViews.zoomIn')">+</button>
                <button class="ancestor-person-cards-view__control-btn ancestor-person-cards-view__control-btn--wide"
                    @click="resetView" :aria-label="$t('treeViews.resetView')">{{ $t('treeViews.reset') }}</button>

                <div class="ancestor-person-cards-view__pan-pad">
                    <button class="ancestor-person-cards-view__control-btn" @click="panBy(0, 80)"
                        :aria-label="$t('treeViews.moveUp')">↑</button>
                    <button class="ancestor-person-cards-view__control-btn" @click="panBy(80, 0)"
                        :aria-label="$t('treeViews.moveLeft')">←</button>
                    <button class="ancestor-person-cards-view__control-btn" @click="panBy(-80, 0)"
                        :aria-label="$t('treeViews.moveRight')">→</button>
                    <button class="ancestor-person-cards-view__control-btn" @click="panBy(0, -80)"
                        :aria-label="$t('treeViews.moveDown')">↓</button>
                </div>
            </div>

            <div ref="scene" class="ancestor-canvas-scene" :style="sceneStyle">
                <svg class="ancestor-canvas-scene__lines" :width="layout.width" :height="layout.height"
                    :viewBox="`0 0 ${layout.width} ${layout.height}`" preserveAspectRatio="none">
                    <path v-for="(line, index) in layout.lines" :key="`line-${index}`" :d="line.path"
                        class="ancestor-canvas-scene__line" :class="line.className" :style="line.style" />
                </svg>

                <div v-for="band in layout.familyBands" :key="band.layoutId" class="ancestor-canvas-scene__family-band"
                    :class="band.className" :style="band.style">
                    <div class="ancestor-canvas-scene__family-band-label">{{ band.label }}</div>
                </div>

                <div v-for="node in layout.nodes" :key="node.layoutId" class="ancestor-canvas-scene__card" :class="{
                    'ancestor-canvas-scene__card--dimmed': isCardDimmed(node.layoutId),
                    'ancestor-canvas-scene__card--lineage': isCardHighlighted(node.layoutId)
                }" :style="node.style" @mouseenter="expandTreeCard(node, $event)" @mouseleave="clearHoveredLineage"
                    @focusin="expandTreeCard(node, $event)" @focusout="clearHoveredLineage">
                    <PersonCard :photoSrc="node.card.photoSrc" :name="node.card.name" :years="node.card.years"
                        :gender="node.card.gender" :href="node.card.href" :isSmall="true" :isSelf="node.personID === personId"
                        :isDefault="false" :personID="node.card.personID" :firstname="node.card.firstname"
                        :lastname="node.card.lastname" :living="node.card.living"
                        :placeholder="node.card.placeholder" />
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import * as d3 from 'd3';
import html2canvas from 'html2canvas';
import { getAncestors, getDescendants, getSiblings } from '@/api/personService';
import { useAuth } from '@/composables/useAuth';
import { useLocale } from '@/composables/useLocale';
import PersonCard from '@/atoms/PersonCard.vue';
import { extractSortableDate, formatYears } from '@/utils/dateUtils';
import { getPhotoSrcWithFallback } from '@/utils/photoUtils';
import { getPersonNodeKey, isLivingPlaceholder, isPrivateLivingPerson } from '@/utils/personPrivacy';

export default {
    name: 'AncestorTreePersonCards',
    components: {
        PersonCard
    },
    props: {
        personId: {
            type: String,
            required: true
        },
        selectedGenerations: {
            type: Number,
            default: 4
        },
        fitToContent: {
            type: Boolean,
            default: false
        },
        maxFitZoom: {
            type: Number,
            default: 2.4
        },
        cooperativeGestures: {
            type: Boolean,
            default: true
        },
        treeMode: {
            type: String,
            default: 'ancestors',
            validator: value => ['ancestors', 'descendants'].includes(value)
        },
        familyGroups: {
            type: Array,
            default: () => []
        },
        initialRootPerson: {
            type: Object,
            default: null
        },
        includeOriginSiblings: {
            type: Boolean,
            default: false
        }
    },
    emits: ['layout-change'],
    setup() {
        const { authToken } = useAuth();
        const { localePath } = useLocale();

        return {
            authToken,
            localePath
        };
    },
    data() {
        return {
            rootPerson: null,
            loading: false,
            error: '',
            pan: { x: 50, y: 40 },
            zoom: 0.75,
            isPanning: false,
            panOrigin: { x: 0, y: 0 },
            isPinching: false,
            lastPinchDistance: 0,
            lastTouchCenter: null,
            hoveredLineageIds: [],
            CARD_WIDTH: 280,
            CARD_HEIGHT: 112,
            CONNECTOR_OVERLAP: 2,
            LEVEL_GAP: 210,
            HORIZONTAL_GAP: 24,
            SIBLING_SEPARATION: 1,
            BRANCH_SEPARATION: 1,
            SCENE_PADDING: 140,
            MIN_ZOOM: 0.01,
            MAX_ZOOM: 2.4,
            FIT_PADDING: 24,
            DEFAULT_PAN: { x: 50, y: 40 },
            DEFAULT_ZOOM: 0.75,
            DESCENDANT_VISUAL_LEVEL_GAP: 214,
            DESCENDANT_DEEP_VISUAL_LEVEL_GAP: 248,
            DESCENDANT_FAMILY_LANE_GAP: 248,
            DESCENDANT_FAMILY_SEPARATION: 2.65,
            DESCENDANT_FAMILY_BAND_PADDING_X: 44,
            DESCENDANT_FAMILY_BAND_PADDING_TOP: 44,
            DESCENDANT_FAMILY_BAND_PADDING_BOTTOM: 44,
            DESCENDANT_MIN_READABLE_FIT_ZOOM: 0.34,
            DESCENDANT_INITIAL_ZOOM_FACTOR: Math.pow(0.88, 5),
            FAMILY_JUNCTION_GAP: 28,
            originSiblingGroups: [],
            fetchRequestId: 0,
            descendantAbortController: null
        };
    },
    computed: {
        sceneStyle() {
            return {
                width: `${this.layout.width}px`,
                height: `${this.layout.height}px`,
                transform: `translate(${this.pan.x}px, ${this.pan.y}px) scale(${this.zoom})`
            };
        },
        isDescendantTree() {
            return this.treeMode === 'descendants';
        },
        childRelationKey() {
            return this.isDescendantTree ? 'descendants' : 'ancestors';
        },
        loadingLabel() {
            return this.isDescendantTree
                ? this.$t('treeViews.loadingDescendants')
                : this.$t('treeViews.loadingAncestors');
        },
        loadErrorLabel() {
            return this.isDescendantTree
                ? this.$t('treeViews.descendantsLoadError')
                : this.$t('treeViews.ancestorsLoadError');
        },
        visibleDescendantGenerations() {
            const generations = Number(this.selectedGenerations);
            return Number.isFinite(generations) && generations > 0
                ? generations
                : 1;
        },
        descendantFetchGenerations() {
            return Math.max(0, this.visibleDescendantGenerations - 1);
        },
        layout() {
            if (!this.rootPerson) {
                return {
                    width: 0,
                    height: 0,
                    contentBounds: {
                        x: 0,
                        y: 0,
                        width: 0,
                        height: 0
                    },
                    nodes: [],
                    lines: [],
                    familyBands: []
                };
            }

            if (this.isDescendantTree) {
                return this.buildDescendantLayout();
            }

            return this.buildAncestorLayout();
        }
    },
    watch: {
        personId: {
            immediate: true,
            handler() {
                this.fetchTree();
            }
        },
        selectedGenerations() {
            this.fetchTree();
        },
        treeMode() {
            this.fetchTree();
        },
        includeOriginSiblings() {
            this.fetchTree();
        },
        familyGroups() {
            if (this.isDescendantTree && this.visibleDescendantGenerations === 1) {
                this.fetchTree();
            }
        },
        initialRootPerson() {
            if (this.isDescendantTree && this.visibleDescendantGenerations === 1) {
                this.fetchTree();
            }
        }
    },
    beforeUnmount() {
        this.descendantAbortController?.abort();
    },
    methods: {
        buildAncestorLayout() {
            const hierarchyRoot = d3.hierarchy(this.rootPerson, person => {
                return this.getHierarchyChildren(person);
            });

            hierarchyRoot.each(node => {
                // Use a structural path ID to avoid collisions when the same personID appears in multiple branches.
                const branchPath = [];
                let current = node;

                while (current.parent) {
                    branchPath.push(current.parent.children.indexOf(current));
                    current = current.parent;
                }

                node._layoutId = `n-${branchPath.reverse().join('') || 'root'}`;
            });

            const maxDepth = Math.max(
                hierarchyRoot.height,
                Math.max(this.selectedGenerations, 1)
            );

            const treeLayout = d3
                .tree()
                .separation((a, b) => {
                    return a.parent === b.parent
                        ? this.SIBLING_SEPARATION
                        : this.BRANCH_SEPARATION;
                })
                .nodeSize([this.CARD_WIDTH + this.HORIZONTAL_GAP, this.LEVEL_GAP]);

            treeLayout(hierarchyRoot);

            const descendants = hierarchyRoot.descendants();
            const minX = d3.min(descendants, node => node.x) || 0;
            const maxX = d3.max(descendants, node => node.x) || 0;

            const baseWidth = maxX - minX + this.CARD_WIDTH + this.SCENE_PADDING * 2;
            const baseHeight = maxDepth * this.LEVEL_GAP + this.CARD_HEIGHT + this.SCENE_PADDING * 2;
            const centerX = this.SCENE_PADDING + this.CARD_WIDTH / 2;
            const baseX = centerX - minX;
            const baseY = this.SCENE_PADDING;

            let positionedNodes = descendants.map(node => {
                const xCenter = baseX + node.x;
                const yCenter =
                    baseY
                    + (maxDepth - node.depth) * this.LEVEL_GAP
                    + this.CARD_HEIGHT / 2;

                return this.createPositionedPersonNode({
                    person: node.data,
                    depth: node.depth,
                    layoutId: node._layoutId,
                    lineageIds: node.ancestors().map(ancestorNode => ancestorNode._layoutId),
                    xCenter,
                    yCenter
                });
            });

            const nodeMap = new Map(positionedNodes.map(node => [node.layoutId, node]));
            const hasFullSiblingGroup = this.originSiblingGroups.some(group => group.type === 'full');
            let lines = hierarchyRoot.links().flatMap(link => {
                if (hasFullSiblingGroup && link.source.depth === 0 && link.target.depth === 1) {
                    return [];
                }

                const source = nodeMap.get(link.source._layoutId);
                const target = nodeMap.get(link.target._layoutId);

                if (!source || !target) {
                    return [];
                }

                const startX = source.xCenter;
                const startY = source.yCenter - this.CARD_HEIGHT / 2 + this.CONNECTOR_OVERLAP;
                const endX = target.xCenter;
                const endY = target.yCenter + this.CARD_HEIGHT / 2 - this.CONNECTOR_OVERLAP;
                const middleY = (startY + endY) / 2;

                return [
                    {
                        path: `M ${startX} ${startY} L ${startX} ${middleY} L ${endX} ${middleY} L ${endX} ${endY}`
                    }
                ];
            });

            const originLayout = this.buildOriginSiblingLayout(positionedNodes);
            positionedNodes = positionedNodes.concat(originLayout.nodes);
            lines = lines.concat(originLayout.lines);

            return this.finalizeLayout({
                nodes: positionedNodes,
                familyBands: originLayout.familyBands,
                lines,
                minWidth: baseWidth,
                minHeight: baseHeight
            });
        },
        createPositionedPersonNode({ person, depth, layoutId, lineageIds, xCenter, yCenter }) {
            return {
                person,
                depth,
                layoutId,
                lineageIds,
                personID: getPersonNodeKey(person),
                xCenter,
                yCenter,
                style: this.getNodeStyle(xCenter, yCenter),
                card: this.toPersonCardProps(person)
            };
        },
        getNodeStyle(xCenter, yCenter) {
            return {
                left: `${xCenter - this.CARD_WIDTH / 2}px`,
                top: `${yCenter - this.CARD_HEIGHT / 2}px`,
                width: `${this.CARD_WIDTH}px`
            };
        },
        buildOriginSiblingLayout(positionedNodes) {
            if (!this.includeOriginSiblings || this.isDescendantTree || !this.originSiblingGroups.length) {
                return {
                    nodes: [],
                    familyBands: [],
                    lines: []
                };
            }

            const rootNode = positionedNodes.find(node => node.personID === this.rootPerson?.personID && node.depth === 0);

            if (!rootNode) {
                return {
                    nodes: [],
                    familyBands: [],
                    lines: []
                };
            }

            const nodeByPersonId = positionedNodes.reduce((map, node) => {
                if (node.personID && !map.has(node.personID)) {
                    map.set(node.personID, node);
                }
                return map;
            }, new Map());
            const extraNodes = [];
            const familyBands = [];
            const lines = [];
            const groupPlacements = this.getOriginSiblingGroupPlacements(rootNode, nodeByPersonId);
            const slotWidth = this.CARD_WIDTH + this.HORIZONTAL_GAP;

            groupPlacements.forEach((placement) => {
                const { group, groupIndex, parentNodes, children, childY, startX } = placement;
                const childNodes = children.map((person, childIndex) => {
                    if (person.personID === this.rootPerson?.personID) {
                        return rootNode;
                    }

                    const xCenter = startX + childIndex * slotWidth;
                    const siblingNode = this.createPositionedPersonNode({
                        person,
                        depth: 0,
                        layoutId: `origin-${group.type}-${person.personID || childIndex}`,
                        lineageIds: [
                            `origin-band-${group.type}`,
                            ...parentNodes.map(parentNode => parentNode.layoutId),
                            `origin-${group.type}-${person.personID || childIndex}`
                        ],
                        xCenter,
                        yCenter: childY
                    });

                    extraNodes.push(siblingNode);
                    return siblingNode;
                });

                const originFamilyLayout = this.buildOriginFamilyUnitLayout({
                    group,
                    groupIndex,
                    parentNodes,
                    childNodes
                });

                familyBands.push(originFamilyLayout.familyBand);
                lines.push(...originFamilyLayout.lines);
            });

            return {
                nodes: extraNodes,
                familyBands,
                lines
            };
        },
        getOriginSiblingGroupPlacements(rootNode, nodeByPersonId) {
            const slotWidth = this.CARD_WIDTH + this.HORIZONTAL_GAP;
            const fullPlacements = [];
            const halfPlacements = [];

            this.originSiblingGroups.forEach((group, groupIndex) => {
                const parentNodes = group.parentIds
                    .map(parentId => nodeByPersonId.get(parentId))
                    .filter(Boolean);

                if (parentNodes.length === 0 || group.children.length === 0) {
                    return;
                }

                const children = this.sortPeopleByBirthDate(group.children);
                const parentAnchorX = d3.mean(parentNodes, node => node.xCenter) || rootNode.xCenter;
                const groupWidth = this.CARD_WIDTH + Math.max(0, children.length - 1) * slotWidth;

                if (group.type === 'full') {
                    const rootChildIndex = Math.max(
                        0,
                        children.findIndex(child => child.personID === this.rootPerson?.personID)
                    );
                    const startX = rootNode.xCenter - rootChildIndex * slotWidth;
                    const childLeft = startX - this.CARD_WIDTH / 2;
                    const childRight = startX + (children.length - 1) * slotWidth + this.CARD_WIDTH / 2;
                    const bandLeftAnchor = Math.min(childLeft, parentAnchorX - this.CARD_WIDTH / 2);
                    const bandRightAnchor = Math.max(childRight, parentAnchorX + this.CARD_WIDTH / 2);

                    fullPlacements.push({
                        group,
                        groupIndex,
                        parentNodes,
                        children,
                        childY: rootNode.yCenter,
                        startX,
                        anchorX: rootNode.xCenter,
                        left: childLeft,
                        right: childRight,
                        lockedBandLeft: bandLeftAnchor - this.DESCENDANT_FAMILY_BAND_PADDING_X,
                        lockedBandRight: bandRightAnchor + this.DESCENDANT_FAMILY_BAND_PADDING_X
                    });
                    return;
                }

                halfPlacements.push({
                    group,
                    groupIndex,
                    parentNodes,
                    children,
                    childY: rootNode.yCenter,
                    groupWidth,
                    anchorX: parentAnchorX,
                    left: parentAnchorX - groupWidth / 2,
                    right: parentAnchorX + groupWidth / 2
                });
            });

            return fullPlacements.concat(this.alignHalfSiblingPlacements(halfPlacements, fullPlacements, rootNode));
        },
        alignHalfSiblingPlacements(placements, lockedPlacements = [], rootNode = null) {
            const bandPadding = this.DESCENDANT_FAMILY_BAND_PADDING_X;
            const minGap = Math.max(56, this.HORIZONTAL_GAP * 2);
            const activeLockedPlacements = lockedPlacements.length > 0 || !rootNode
                ? lockedPlacements
                : [{
                    lockedBandLeft: rootNode.xCenter - this.CARD_WIDTH / 2 - bandPadding,
                    lockedBandRight: rootNode.xCenter + this.CARD_WIDTH / 2 + bandPadding
                }];

            if (activeLockedPlacements.length > 0 && rootNode) {
                const lockedLeft = d3.min(activeLockedPlacements, placement => placement.lockedBandLeft) || 0;
                const lockedRight = d3.max(activeLockedPlacements, placement => placement.lockedBandRight) || 0;
                const leftPlacements = placements
                    .filter(placement => placement.anchorX < rootNode.xCenter)
                    .sort((a, b) => b.anchorX - a.anchorX);
                const rightPlacements = placements
                    .filter(placement => placement.anchorX >= rootNode.xCenter)
                    .sort((a, b) => a.anchorX - b.anchorX);
                let nextLeftBandRight = lockedLeft - minGap;
                let nextRightBandLeft = lockedRight + minGap;

                leftPlacements.forEach(placement => {
                    const bandRight = placement.right + bandPadding;

                    if (bandRight > nextLeftBandRight) {
                        const shift = bandRight - nextLeftBandRight;
                        placement.left -= shift;
                        placement.right -= shift;
                    }

                    nextLeftBandRight = placement.left - bandPadding - minGap;
                });

                rightPlacements.forEach(placement => {
                    const bandLeft = placement.left - bandPadding;

                    if (bandLeft < nextRightBandLeft) {
                        const shift = nextRightBandLeft - bandLeft;
                        placement.left += shift;
                        placement.right += shift;
                    }

                    nextRightBandLeft = placement.right + bandPadding + minGap;
                });

                return leftPlacements
                    .concat(rightPlacements)
                    .sort((a, b) => a.left - b.left)
                    .map(placement => ({
                        ...placement,
                        startX: placement.left + this.CARD_WIDTH / 2
                    }));
            }

            if (placements.length <= 1) {
                return placements.map(placement => ({
                    ...placement,
                    startX: placement.left + this.CARD_WIDTH / 2
                }));
            }

            const sortedPlacements = placements
                .slice()
                .sort((a, b) => a.left - b.left);

            let previousBandRight = -Infinity;
            sortedPlacements.forEach(placement => {
                const bandLeft = placement.left - bandPadding;
                const minBandLeft = previousBandRight + minGap;

                if (bandLeft < minBandLeft) {
                    const shift = minBandLeft - bandLeft;
                    placement.left += shift;
                    placement.right += shift;
                }

                previousBandRight = placement.right + bandPadding;
            });

            const desiredCenter = d3.mean(sortedPlacements, placement => placement.anchorX) || sortedPlacements[0].anchorX;
            const currentCenter = (sortedPlacements[0].left + sortedPlacements[sortedPlacements.length - 1].right) / 2;
            const recenterShift = desiredCenter - currentCenter;

            return sortedPlacements.map(placement => {
                const left = placement.left + recenterShift;

                return {
                    ...placement,
                    left,
                    right: placement.right + recenterShift,
                    startX: left + this.CARD_WIDTH / 2
                };
            });
        },
        buildOriginFamilyUnitLayout({ group, groupIndex, parentNodes, childNodes }) {
            const generation = 0;
            const accent = this.getFamilyAccent(groupIndex + 2, generation);
            const depthStyle = this.getFamilyDepthStyle(accent, generation);
            const minChildX = d3.min(childNodes, node => node.xCenter) || 0;
            const maxChildX = d3.max(childNodes, node => node.xCenter) || 0;
            const minParentX = d3.min(parentNodes, node => node.xCenter) || minChildX;
            const maxParentX = d3.max(parentNodes, node => node.xCenter) || maxChildX;
            const parentBottom = d3.max(parentNodes, node => node.yCenter + this.CARD_HEIGHT / 2 - this.CONNECTOR_OVERLAP) || 0;
            const childTop = d3.min(childNodes, node => node.yCenter - this.CARD_HEIGHT / 2 + this.CONNECTOR_OVERLAP) || parentBottom;
            const junctionY = group.type === 'full'
                ? Math.min(
                    childTop - 28,
                    Math.max(parentBottom + this.FAMILY_JUNCTION_GAP, parentBottom + (childTop - parentBottom) * 0.62)
                )
                : childTop - 28;
            const parentRailY = Math.max(parentBottom + 22, Math.min(junctionY - 18, parentBottom + (junctionY - parentBottom) * 0.48));
            const parentAnchorX = d3.mean(parentNodes, node => node.xCenter) || minChildX;
            const childAnchorX = d3.mean(childNodes, node => node.xCenter) || minChildX;
            const shouldFrameParentAnchor = group.type === 'full';
            const bandLeftAnchor = shouldFrameParentAnchor
                ? Math.min(minChildX, parentAnchorX)
                : minChildX;
            const bandRightAnchor = shouldFrameParentAnchor
                ? Math.max(maxChildX, parentAnchorX)
                : maxChildX;
            const bandLeft = bandLeftAnchor - this.CARD_WIDTH / 2 - this.DESCENDANT_FAMILY_BAND_PADDING_X;
            const bandRight = bandRightAnchor + this.CARD_WIDTH / 2 + this.DESCENDANT_FAMILY_BAND_PADDING_X;
            const bandTop = junctionY - this.DESCENDANT_FAMILY_BAND_PADDING_TOP;
            const bandBottom = d3.max(childNodes, node => node.yCenter + this.CARD_HEIGHT / 2) + this.DESCENDANT_FAMILY_BAND_PADDING_BOTTOM;
            const lines = [];

            if (parentNodes.length > 1) {
                lines.push({
                    path: `M ${minParentX} ${parentRailY} L ${maxParentX} ${parentRailY}`,
                    className: 'ancestor-canvas-scene__line--family-rail',
                    style: { '--family-accent': accent }
                });

                parentNodes.forEach(parentNode => {
                    lines.push({
                        path: `M ${parentNode.xCenter} ${parentBottom} L ${parentNode.xCenter} ${parentRailY}`,
                        className: 'ancestor-canvas-scene__line--family-parent',
                        style: { '--family-accent': accent }
                    });
                });

                lines.push({
                    path: `M ${parentAnchorX} ${parentRailY} L ${parentAnchorX} ${junctionY}`,
                    className: 'ancestor-canvas-scene__line--family-parent',
                    style: { '--family-accent': accent }
                });
            } else {
                const parentNode = parentNodes[0];
                const connectorAnchorX = group.type === 'full'
                    ? parentNode.xCenter
                    : childAnchorX;

                lines.push({
                    path: connectorAnchorX === parentNode.xCenter
                        ? `M ${parentNode.xCenter} ${parentBottom} L ${parentNode.xCenter} ${junctionY}`
                        : `M ${parentNode.xCenter} ${parentBottom} L ${parentNode.xCenter} ${parentRailY} L ${connectorAnchorX} ${parentRailY} L ${connectorAnchorX} ${junctionY}`,
                    className: 'ancestor-canvas-scene__line--family-parent',
                    style: { '--family-accent': accent }
                });
            }

            lines.push({
                path: `M ${minChildX} ${junctionY} L ${maxChildX} ${junctionY}`,
                className: 'ancestor-canvas-scene__line--family-rail',
                style: { '--family-accent': accent }
            });

            childNodes.forEach(childNode => {
                lines.push({
                    path: `M ${childNode.xCenter} ${junctionY} L ${childNode.xCenter} ${childTop}`,
                    className: 'ancestor-canvas-scene__line--family-child',
                    style: { '--family-accent': accent }
                });
            });

            return {
                familyBand: {
                    layoutId: `origin-band-${group.type}`,
                    label: group.label,
                    bounds: {
                        minX: bandLeft,
                        maxX: bandRight,
                        minY: bandTop,
                        maxY: bandBottom
                    },
                    className: `ancestor-canvas-scene__family-band--origin ancestor-canvas-scene__family-band--theme-${groupIndex % 6}`,
                    style: {
                        left: `${bandLeft}px`,
                        top: `${bandTop}px`,
                        width: `${bandRight - bandLeft}px`,
                        height: `${bandBottom - bandTop}px`,
                        '--family-accent': accent,
                        ...depthStyle
                    }
                },
                lines
            };
        },
        finalizeLayout({ nodes, familyBands, lines, minWidth, minHeight }) {
            const contentBounds = this.getContentBounds(nodes.concat(familyBands));
            const offsetX = Number.isFinite(contentBounds.minX) && contentBounds.minX < this.SCENE_PADDING
                ? this.SCENE_PADDING - contentBounds.minX
                : 0;
            const offsetY = Number.isFinite(contentBounds.minY) && contentBounds.minY < this.SCENE_PADDING
                ? this.SCENE_PADDING - contentBounds.minY
                : 0;
            const shiftedNodes = nodes.map(node => this.offsetNode(node, offsetX, offsetY));
            const shiftedFamilyBands = familyBands.map(band => this.offsetBand(band, offsetX, offsetY));
            const shiftedLines = lines.map(line => ({
                ...line,
                path: this.offsetSvgPath(line.path, offsetX, offsetY)
            }));
            const shiftedBounds = this.getContentBounds(shiftedNodes.concat(shiftedFamilyBands));
            const width = Math.max(
                minWidth + offsetX,
                (Number.isFinite(shiftedBounds.maxX) ? shiftedBounds.maxX : 0) + this.SCENE_PADDING
            );
            const height = Math.max(
                minHeight + offsetY,
                (Number.isFinite(shiftedBounds.maxY) ? shiftedBounds.maxY : 0) + this.SCENE_PADDING
            );

            return {
                width,
                height,
                contentBounds: {
                    x: Number.isFinite(shiftedBounds.minX) ? shiftedBounds.minX : 0,
                    y: Number.isFinite(shiftedBounds.minY) ? shiftedBounds.minY : 0,
                    width: Number.isFinite(shiftedBounds.maxX - shiftedBounds.minX)
                        ? shiftedBounds.maxX - shiftedBounds.minX
                        : 0,
                    height: Number.isFinite(shiftedBounds.maxY - shiftedBounds.minY)
                        ? shiftedBounds.maxY - shiftedBounds.minY
                        : 0
                },
                nodes: shiftedNodes,
                familyBands: shiftedFamilyBands,
                lines: shiftedLines
            };
        },
        offsetNode(node, offsetX, offsetY) {
            if (!offsetX && !offsetY) {
                return node;
            }

            const xCenter = node.xCenter + offsetX;
            const yCenter = node.yCenter + offsetY;

            return {
                ...node,
                xCenter,
                yCenter,
                style: this.getNodeStyle(xCenter, yCenter)
            };
        },
        offsetBand(band, offsetX, offsetY) {
            if (!offsetX && !offsetY) {
                return band;
            }

            const bounds = {
                minX: band.bounds.minX + offsetX,
                maxX: band.bounds.maxX + offsetX,
                minY: band.bounds.minY + offsetY,
                maxY: band.bounds.maxY + offsetY
            };

            return {
                ...band,
                bounds,
                style: {
                    ...band.style,
                    left: `${bounds.minX}px`,
                    top: `${bounds.minY}px`,
                    width: `${bounds.maxX - bounds.minX}px`,
                    height: `${bounds.maxY - bounds.minY}px`
                }
            };
        },
        offsetSvgPath(path, offsetX, offsetY) {
            if (!offsetX && !offsetY) {
                return path;
            }

            let coordinateIndex = 0;

            return String(path).replace(/-?\d+(?:\.\d+)?/g, value => {
                const numericValue = Number(value);
                const offset = coordinateIndex % 2 === 0 ? offsetX : offsetY;
                coordinateIndex += 1;
                return `${numericValue + offset}`;
            });
        },
        sortPeopleByBirthDate(people) {
            return (people || []).slice().sort((a, b) => {
                const dateA = extractSortableDate(a.birthdatetr || a.birthdate)
                    || extractSortableDate(a.altbirthdatetr || a.altbirthdate);
                const dateB = extractSortableDate(b.birthdatetr || b.birthdate)
                    || extractSortableDate(b.altbirthdatetr || b.altbirthdate);

                if (dateA && dateB) {
                    return dateA - dateB;
                }

                if (dateA) {
                    return -1;
                }

                if (dateB) {
                    return 1;
                }

                return 0;
            });
        },
        uniquePeopleById(people) {
            const seen = new Set();

            return (people || []).filter(person => {
                const personId = getPersonNodeKey(person);

                if (!personId || seen.has(personId)) {
                    return false;
                }

                seen.add(personId);
                return true;
            });
        },
        getDirectAncestorBySex(rootPerson, sex) {
            const ancestors = Array.isArray(rootPerson?.ancestors) ? rootPerson.ancestors : [];

            return ancestors.find(ancestor => ancestor?.sex === sex && ancestor?.personID) || null;
        },
        getRootParentIds(rootPerson, siblingsData) {
            const fatherAncestor = this.getDirectAncestorBySex(rootPerson, 'M');
            const motherAncestor = this.getDirectAncestorBySex(rootPerson, 'F');
            const firstFamilyMember = (siblingsData || []).find(sibling => (
                rootPerson?.familyID
                    ? sibling.familyID === rootPerson.familyID
                    : sibling.husband || sibling.wife
            ));

            return {
                fatherId: rootPerson?.fatherID || fatherAncestor?.personID || firstFamilyMember?.husband || '',
                motherId: rootPerson?.motherID || motherAncestor?.personID || firstFamilyMember?.wife || ''
            };
        },
        normalizeOriginSiblingPerson(person) {
            if (!person) {
                return person;
            }

            if (person.personID === this.rootPerson?.personID) {
                return {
                    ...this.rootPerson,
                    ...person,
                    profileImage: this.rootPerson.profileImage,
                    photoSrc: this.rootPerson.photoSrc,
                    photoPath: this.rootPerson.photoPath
                };
            }

            return person;
        },
        buildOriginSiblingGroups(rootPerson, siblingsData) {
            if (!this.includeOriginSiblings || !rootPerson || !Array.isArray(siblingsData)) {
                return [];
            }

            const { fatherId, motherId } = this.getRootParentIds(rootPerson, siblingsData);
            const inferredRootFamily = siblingsData.find(sibling => (
                (!fatherId || sibling.husband === fatherId)
                && (!motherId || sibling.wife === motherId)
            ));
            const rootFamilyId = rootPerson.familyID || inferredRootFamily?.familyID;
            const fullSiblings = siblingsData.filter(sibling => (
                sibling.siblingType === 'full'
                || (rootFamilyId && sibling.familyID === rootFamilyId)
            ));
            const paternalHalfSiblings = fatherId
                ? siblingsData.filter(sibling => sibling.siblingType === 'paternal-half'
                    || (sibling.familyID !== rootFamilyId && sibling.husband === fatherId))
                : siblingsData.filter(sibling => sibling.siblingType === 'paternal-half');
            const maternalHalfSiblings = motherId
                ? siblingsData.filter(sibling => sibling.siblingType === 'maternal-half'
                    || (sibling.familyID !== rootFamilyId && sibling.wife === motherId))
                : siblingsData.filter(sibling => sibling.siblingType === 'maternal-half');
            const groups = [];

            if (fullSiblings.length > 0) {
                groups.push({
                    type: 'full',
                    label: this.$t('siblingSection.sectionTitle'),
                    parentIds: [fatherId, motherId].filter(Boolean),
                    children: this.uniquePeopleById(
                        [rootPerson, ...fullSiblings].map(person => this.normalizeOriginSiblingPerson(person))
                    )
                });
            }

            if (paternalHalfSiblings.length > 0) {
                groups.push({
                    type: 'paternal-half',
                    label: this.$t('siblingSection.halfSiblingsFather'),
                    parentIds: [fatherId].filter(Boolean),
                    children: this.uniquePeopleById(
                        paternalHalfSiblings.map(person => this.normalizeOriginSiblingPerson(person))
                    )
                });
            }

            if (maternalHalfSiblings.length > 0) {
                groups.push({
                    type: 'maternal-half',
                    label: this.$t('siblingSection.halfSiblingsMother'),
                    parentIds: [motherId].filter(Boolean),
                    children: this.uniquePeopleById(
                        maternalHalfSiblings.map(person => this.normalizeOriginSiblingPerson(person))
                    )
                });
            }

            return groups.filter(group => group.children.length > 0);
        },
        async fetchOriginSiblingsData(personId) {
            if (!this.includeOriginSiblings || this.isDescendantTree) {
                return [];
            }

            try {
                return await getSiblings(personId);
            } catch {
                return [];
            }
        },
        getContentBounds(items) {
            return items.reduce((bounds, item) => {
                const itemBounds = item.bounds || {
                    minX: item.xCenter - this.CARD_WIDTH / 2,
                    maxX: item.xCenter + this.CARD_WIDTH / 2,
                    minY: item.yCenter - this.CARD_HEIGHT / 2,
                    maxY: item.yCenter + this.CARD_HEIGHT / 2
                };

                return {
                    minX: Math.min(bounds.minX, itemBounds.minX),
                    maxX: Math.max(bounds.maxX, itemBounds.maxX),
                    minY: Math.min(bounds.minY, itemBounds.minY),
                    maxY: Math.max(bounds.maxY, itemBounds.maxY)
                };
            }, {
                minX: Infinity,
                maxX: -Infinity,
                minY: Infinity,
                maxY: -Infinity
            });
        },
        buildDescendantVisualNode(person, generation = 0) {
            const familyGroups = this.getPersonFamilyGroups(person)
                .map((family, familyIndex) => ({
                    family,
                    familyIndex,
                    familyID: family?.familyID || `${getPersonNodeKey(person, 'person')}-family-${familyIndex}`,
                    children: Array.isArray(family?.children) ? family.children.filter(Boolean) : []
                }))
                .filter(group => group.children.length > 0);
            const fallbackChildren = Array.isArray(person?.descendants)
                ? person.descendants.filter(Boolean)
                : [];
            const fallbackChildrenById = new Map(fallbackChildren.map(child => [getPersonNodeKey(child), child]));
            const hasFamilyGroups = familyGroups.length > 0;

            return {
                type: 'person',
                person,
                personID: getPersonNodeKey(person),
                generation,
                children: hasFamilyGroups
                    ? familyGroups.map(group => ({
                        type: 'family',
                        family: group.family,
                        familyID: group.familyID,
                        familyIndex: group.familyIndex,
                        parentPersonID: getPersonNodeKey(person),
                        generation,
                        children: group.children.map(child => this.buildDescendantVisualNode(
                            this.mergeFamilyChildWithDescendant(child, fallbackChildrenById.get(getPersonNodeKey(child))),
                            generation + 1
                        ))
                    }))
                    : fallbackChildren.map(child => this.buildDescendantVisualNode(child, generation + 1))
            };
        },
        mergeFamilyChildWithDescendant(familyChild, descendantChild) {
            if (!descendantChild) {
                return familyChild;
            }

            return {
                ...descendantChild,
                ...familyChild,
                firstname: familyChild.firstname ?? descendantChild.firstname,
                lastname: familyChild.lastname ?? descendantChild.lastname,
                birthdatetr: familyChild.birthdatetr ?? descendantChild.birthdatetr,
                altbirthdatetr: familyChild.altbirthdatetr ?? descendantChild.altbirthdatetr,
                deathdatetr: familyChild.deathdatetr ?? descendantChild.deathdatetr,
                burialdatetr: familyChild.burialdatetr ?? descendantChild.burialdatetr,
                living: familyChild.living ?? descendantChild.living,
                photoPath: familyChild.photoPath ?? descendantChild.photoPath,
                photoSrc: familyChild.photoSrc ?? descendantChild.photoSrc,
                years: familyChild.years ?? descendantChild.years,
                descendants: descendantChild.descendants,
                descendantFamilies: descendantChild.descendantFamilies
            };
        },
        getDescendantSeparation(a, b) {
            if (a.data.type === 'family' || b.data.type === 'family') {
                return a.parent === b.parent
                    ? this.DESCENDANT_FAMILY_SEPARATION
                    : this.DESCENDANT_FAMILY_SEPARATION + 0.6;
            }

            if (a.parent === b.parent) {
                return this.SIBLING_SEPARATION;
            }

            return this.BRANCH_SEPARATION + 0.8;
        },
        getDescendantVisualLevelGap() {
            return this.visibleDescendantGenerations > 1
                ? this.DESCENDANT_DEEP_VISUAL_LEVEL_GAP
                : this.DESCENDANT_VISUAL_LEVEL_GAP;
        },
        getDescendantNodeYCenter(node, baseY, descendantLevelGap) {
            const generation = Number.isFinite(Number(node.data.generation))
                ? Number(node.data.generation)
                : 0;
            const generationTop = baseY + generation * descendantLevelGap;

            if (node.data.type === 'family') {
                return generationTop + this.CARD_HEIGHT + this.DESCENDANT_FAMILY_LANE_GAP;
            }

            return generationTop + this.CARD_HEIGHT / 2;
        },
        buildDescendantLayout() {
            const visualRoot = this.buildDescendantVisualNode(this.rootPerson);
            const hierarchyRoot = d3.hierarchy(visualRoot, node => node.children || []);

            hierarchyRoot.each(node => {
                const branchPath = [];
                let current = node;

                while (current.parent) {
                    branchPath.push(current.parent.children.indexOf(current));
                    current = current.parent;
                }

                const path = branchPath.reverse().join('') || 'root';
                node._layoutId = `${node.data.type}-${path}`;
            });

            const descendantLevelGap = this.getDescendantVisualLevelGap();
            const treeLayout = d3
                .tree()
                .separation((a, b) => this.getDescendantSeparation(a, b))
                .nodeSize([this.CARD_WIDTH + this.HORIZONTAL_GAP, descendantLevelGap]);

            treeLayout(hierarchyRoot);

            const visualNodes = hierarchyRoot.descendants();
            const minX = d3.min(visualNodes, node => node.x) || 0;
            const maxX = d3.max(visualNodes, node => node.x) || 0;
            const maxGeneration = d3.max(visualNodes, node => {
                const generation = Number.isFinite(Number(node.data.generation))
                    ? Number(node.data.generation)
                    : 0;

                return node.data.type === 'family' ? generation + 1 : generation;
            }) || 0;
            const width = maxX - minX + this.CARD_WIDTH + this.SCENE_PADDING * 2;
            const height = maxGeneration * descendantLevelGap + this.CARD_HEIGHT + this.SCENE_PADDING * 2;
            const centerX = this.SCENE_PADDING + this.CARD_WIDTH / 2;
            const baseX = centerX - minX;
            const baseY = this.SCENE_PADDING;

            const positionedVisualNodes = visualNodes.map(node => {
                const xCenter = baseX + node.x;
                const yCenter = this.getDescendantNodeYCenter(node, baseY, descendantLevelGap);

                return {
                    visualNode: node,
                    type: node.data.type,
                    person: node.data.person,
                    family: node.data.family,
                    depth: node.depth,
                    generation: node.data.generation,
                    layoutId: node._layoutId,
                    lineageIds: node.ancestors()
                        .filter(ancestorNode => ancestorNode.data.type === 'person')
                        .map(ancestorNode => ancestorNode._layoutId),
                    personID: node.data.personID,
                    parentPersonID: node.data.parentPersonID,
                    familyID: node.data.familyID,
                    familyIndex: node.data.familyIndex,
                    xCenter,
                    yCenter
                };
            });

            const nodeMap = new Map(positionedVisualNodes.map(node => [node.layoutId, node]));
            const positionedPeople = positionedVisualNodes
                .filter(node => node.type === 'person')
                .map(node => ({
                    ...node,
                    style: {
                        left: `${node.xCenter - this.CARD_WIDTH / 2}px`,
                        top: `${node.yCenter - this.CARD_HEIGHT / 2}px`,
                        width: `${this.CARD_WIDTH}px`
                    },
                    card: this.toPersonCardProps(node.person)
                }));
            const familyLayout = this.buildFamilyUnitLayout(positionedVisualNodes, nodeMap);
            const contentBounds = this.getContentBounds(
                positionedPeople
                    .concat(familyLayout.familyBands)
            );

            return {
                width,
                height,
                contentBounds: {
                    x: Number.isFinite(contentBounds.minX) ? contentBounds.minX : 0,
                    y: Number.isFinite(contentBounds.minY) ? contentBounds.minY : 0,
                    width: Number.isFinite(contentBounds.maxX - contentBounds.minX)
                        ? contentBounds.maxX - contentBounds.minX
                        : 0,
                    height: Number.isFinite(contentBounds.maxY - contentBounds.minY)
                        ? contentBounds.maxY - contentBounds.minY
                        : 0
                },
                nodes: positionedPeople,
                familyBands: familyLayout.familyBands,
                lines: familyLayout.lines
            };
        },
        getPersonFamilyGroups(person) {
            if (!this.isDescendantTree || !person) {
                return [];
            }

            const descendantFamilies = Array.isArray(person.descendantFamilies) ? person.descendantFamilies : [];

            if (descendantFamilies.length > 0) {
                return descendantFamilies;
            }

            const personKey = getPersonNodeKey(person);
            const rootKey = getPersonNodeKey(this.rootPerson);
            if (
                personKey
                && rootKey
                && personKey === rootKey
                && Array.isArray(this.familyGroups)
                && this.familyGroups.length > 0
            ) {
                return this.familyGroups;
            }

            return [];
        },
        getFamilySpouseName(family, parentPersonID = this.personId) {
            if (!family) {
                return this.$t('familySection.unknownSpouse');
            }

            const isHusband = family.husband === parentPersonID;
            const spousePlaceholder = isHusband ? family.wifePlaceholder : family.husbandPlaceholder;
            if (isLivingPlaceholder(spousePlaceholder)) return this.$t('privacy.livingPlaceholder');
            const rawName = isHusband ? family.wifeName : family.husbandName;
            const spouseName = String(rawName || '').replace(/\bundefined\b/g, '').trim();

            return spouseName || this.$t('familySection.unknownSpouse');
        },
        getFamilyChildrenLabel(family, parentPersonID = this.personId) {
            return this.$t('familySection.childrenWithSpouse', {
                spouse: this.getFamilySpouseName(family, parentPersonID)
            });
        },
        limitDescendantDepth(person, maxDepth, currentDepth = 0) {
            if (!person) {
                return person;
            }

            if (currentDepth >= maxDepth) {
                return {
                    ...person,
                    descendantFamilies: [],
                    descendants: []
                };
            }

            return {
                ...person,
                descendantFamilies: Array.isArray(person.descendantFamilies)
                    ? person.descendantFamilies
                    : [],
                descendants: Array.isArray(person.descendants)
                    ? person.descendants.map(descendant => this.limitDescendantDepth(descendant, maxDepth, currentDepth + 1))
                    : []
            };
        },
        getHierarchyChildren(person) {
            const relatedPeople = person?.[this.childRelationKey];

            if (!Array.isArray(relatedPeople)) {
                return [];
            }

            if (!this.isDescendantTree) {
                return relatedPeople.slice(0, 2).filter(Boolean);
            }

            return relatedPeople.filter(Boolean);
        },
        buildFamilyUnitLayout(positionedVisualNodes, nodeMap) {
            const familyBands = [];
            const lines = [];
            const familyNodes = positionedVisualNodes.filter(node => node.type === 'family');
            const familyCountByParentLayoutId = familyNodes.reduce((counts, familyNode) => {
                const parentLayoutId = familyNode.visualNode.parent?._layoutId;

                if (!parentLayoutId) {
                    return counts;
                }

                counts.set(parentLayoutId, (counts.get(parentLayoutId) || 0) + 1);
                return counts;
            }, new Map());

            familyNodes.forEach((familyNode, visualFamilyIndex) => {
                const parentNode = nodeMap.get(familyNode.visualNode.parent?._layoutId);
                const childNodes = (familyNode.visualNode.children || [])
                    .map(childNode => nodeMap.get(childNode._layoutId))
                    .filter(Boolean);

                if (!parentNode || childNodes.length === 0) {
                    return;
                }

                const generation = Number.isFinite(Number(familyNode.generation))
                    ? Number(familyNode.generation)
                    : 0;
                const accent = this.getFamilyAccent(visualFamilyIndex, generation);
                const depthStyle = this.getFamilyDepthStyle(accent, generation);
                const minChildX = d3.min(childNodes, node => node.xCenter) || familyNode.xCenter;
                const maxChildX = d3.max(childNodes, node => node.xCenter) || familyNode.xCenter;
                const parentBottom = parentNode.yCenter + this.CARD_HEIGHT / 2 - this.CONNECTOR_OVERLAP;
                const childTop = d3.min(childNodes, node => node.yCenter - this.CARD_HEIGHT / 2 + this.CONNECTOR_OVERLAP) || familyNode.yCenter;
                const labelLaneTop = familyNode.yCenter - this.DESCENDANT_FAMILY_BAND_PADDING_TOP;
                const junctionY = Math.min(
                    childTop - 22,
                    Math.max(familyNode.yCenter, parentBottom + this.FAMILY_JUNCTION_GAP)
                );
                const familyLeadY = parentBottom + Math.max(28, (junctionY - parentBottom) * 0.44);
                const hasMultipleFamilies = familyCountByParentLayoutId.get(parentNode.layoutId) > 1;
                const bandLeft = Math.min(minChildX, familyNode.xCenter) - this.CARD_WIDTH / 2 - this.DESCENDANT_FAMILY_BAND_PADDING_X;
                const bandRight = Math.max(maxChildX, familyNode.xCenter) + this.CARD_WIDTH / 2 + this.DESCENDANT_FAMILY_BAND_PADDING_X;
                const bandTop = Math.min(labelLaneTop, junctionY - this.DESCENDANT_FAMILY_BAND_PADDING_TOP);
                const bandBottom = d3.max(childNodes, node => node.yCenter + this.CARD_HEIGHT / 2) + this.DESCENDANT_FAMILY_BAND_PADDING_BOTTOM;

                familyBands.push({
                    layoutId: `family-band-${familyNode.layoutId}`,
                    label: this.getFamilyChildrenLabel(familyNode.family, familyNode.parentPersonID),
                    bounds: {
                        minX: bandLeft,
                        maxX: bandRight,
                        minY: bandTop,
                        maxY: bandBottom
                    },
                    className: `ancestor-canvas-scene__family-band--theme-${(familyNode.familyIndex || 0) % 6}`,
                    style: {
                        left: `${bandLeft}px`,
                        top: `${bandTop}px`,
                        width: `${bandRight - bandLeft}px`,
                        height: `${bandBottom - bandTop}px`,
                        '--family-accent': accent,
                        ...depthStyle
                    }
                });

                lines.push({
                    path: hasMultipleFamilies
                        ? `M ${parentNode.xCenter} ${parentBottom} L ${parentNode.xCenter} ${familyLeadY} L ${familyNode.xCenter} ${familyLeadY} L ${familyNode.xCenter} ${junctionY}`
                        : `M ${parentNode.xCenter} ${parentBottom} C ${parentNode.xCenter} ${familyLeadY}, ${familyNode.xCenter} ${familyLeadY}, ${familyNode.xCenter} ${junctionY}`,
                    className: 'ancestor-canvas-scene__line--family-parent',
                    style: { '--family-accent': accent }
                });
                lines.push({
                    path: `M ${minChildX} ${junctionY} L ${maxChildX} ${junctionY}`,
                    className: 'ancestor-canvas-scene__line--family-rail',
                    style: { '--family-accent': accent }
                });

                childNodes.forEach(childNode => {
                    lines.push({
                        path: `M ${childNode.xCenter} ${junctionY} L ${childNode.xCenter} ${childTop}`,
                        className: 'ancestor-canvas-scene__line--family-child',
                        style: { '--family-accent': accent }
                    });
                });
            });

            return {
                familyBands,
                lines
            };
        },
        getFamilyAccent(index, generation = 0) {
            const accents = [
                '#f2c14e',
                '#61d394',
                '#4ea5d9',
                '#f28f8f',
                '#c084fc',
                '#f4a261'
            ];

            const normalizedIndex = Number.isFinite(Number(index)) ? Number(index) : 0;
            const normalizedGeneration = Number.isFinite(Number(generation)) ? Number(generation) : 0;

            return accents[(normalizedIndex + normalizedGeneration * 2) % accents.length];
        },
        getFamilyDepthStyle(accent, generation = 0) {
            const normalizedGeneration = Number.isFinite(Number(generation))
                ? Math.max(0, Number(generation))
                : 0;
            const depth = Math.min(normalizedGeneration, 3);
            const strongFillAlpha = 0.11 + depth * 0.035;
            const softFillAlpha = 0.045 + depth * 0.025;
            const borderAlpha = 0.36 + depth * 0.1;
            const labelAlpha = Math.min(0.98, 0.84 + depth * 0.05);

            return {
                '--family-fill-strong': this.hexToRgba(accent, strongFillAlpha),
                '--family-fill-soft': this.hexToRgba(accent, softFillAlpha),
                '--family-border': this.hexToRgba(accent, borderAlpha),
                '--family-label-color': this.hexToRgba(accent, labelAlpha),
                '--family-shadow': `0 ${14 + depth * 2}px ${30 + depth * 4}px rgba(0, 0, 0, ${0.15 + depth * 0.03})`
            };
        },
        hexToRgba(hex, alpha) {
            const normalizedHex = String(hex || '').replace('#', '');

            if (!/^[0-9a-f]{6}$/i.test(normalizedHex)) {
                return `rgba(255, 255, 255, ${alpha})`;
            }

            const red = parseInt(normalizedHex.slice(0, 2), 16);
            const green = parseInt(normalizedHex.slice(2, 4), 16);
            const blue = parseInt(normalizedHex.slice(4, 6), 16);

            return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
        },
        toPersonCardProps(person) {
            const isPlaceholder = isLivingPlaceholder(person);
            const isPrivate = isPrivateLivingPerson(person, this.authToken);
            const displayName = `${person.firstname || ''} ${person.lastname || ''}`.trim()
                || person.name
                || this.$t('treeViews.unknown');
            const gender = person.sex === 'F' ? 'female' : 'male';

            return {
                photoSrc: isPrivate
                    ? ''
                    : getPhotoSrcWithFallback({
                        photoSrc: person.photoSrc,
                        photoPath: person.photoPath,
                        profileImage: person.profileImage,
                        sex: person.sex,
                        gender
                    }),
                name: isPlaceholder
                    ? this.$t('privacy.livingPlaceholder')
                    : isPrivate ? this.$t('nav.privacyHidden') : displayName,
                years: isPrivate
                    ? this.$t('siteStatsOverview.legend.alive')
                    : person.years || formatYears(
                        person.birthdatetr || person.birthdate,
                        person.altbirthdatetr || person.altbirthdate,
                        person.deathdatetr || person.deathdate,
                        person.burialdatetr || person.burialdate,
                        person.living
                    ),
                gender: isPlaceholder ? 'unknown' : gender,
                href: isPlaceholder ? 'javascript:void(0)' : this.localePath(`/person/${person.personID}`),
                personID: person.personID,
                firstname: person.firstname,
                lastname: person.lastname,
                living: person.living,
                placeholder: isPlaceholder,
            };
        },
        startPan(event) {
            if (event.button !== 0) {
                return;
            }

            this.isPanning = true;
            this.panOrigin = {
                x: event.clientX - this.pan.x,
                y: event.clientY - this.pan.y
            };
        },
        onPan(event) {
            if (!this.isPanning) {
                return;
            }

            this.pan.x = event.clientX - this.panOrigin.x;
            this.pan.y = event.clientY - this.panOrigin.y;
        },
        stopPan() {
            this.isPanning = false;
        },
        setHoveredLineage(node) {
            this.hoveredLineageIds = node.lineageIds || [];
        },
        expandTreeCard(node, event) {
            this.setHoveredLineage(node);

            const card = event.currentTarget?.querySelector('.PersonCard');
            const textWrapper = card?.querySelector('.PersonCard_text_wrapper');

            if (!card || !textWrapper) {
                return;
            }

            const cardStyles = window.getComputedStyle(card);
            const paddingWidth = (parseFloat(cardStyles.paddingLeft) || 0)
                + (parseFloat(cardStyles.paddingRight) || 0);
            const gap = parseFloat(cardStyles.columnGap || cardStyles.gap) || 0;
            const flowChildren = Array.from(card.children).filter(child => {
                return window.getComputedStyle(child).position !== 'absolute';
            });
            const contentWidth = flowChildren.reduce((width, child) => {
                if (child !== textWrapper) {
                    return width + child.offsetWidth;
                }

                const textWidth = Array.from(textWrapper.children).reduce((widest, textLine) => {
                    return Math.max(widest, textLine.scrollWidth);
                }, textWrapper.scrollWidth);

                return width + textWidth;
            }, 0);
            const expandedWidth = Math.max(
                this.CARD_WIDTH,
                Math.ceil(paddingWidth + contentWidth + gap * Math.max(0, flowChildren.length - 1))
            );

            card.style.setProperty('--tree-card-expanded-width', `${expandedWidth}px`);
        },
        clearHoveredLineage() {
            this.hoveredLineageIds = [];
        },
        isCardDimmed(layoutId) {
            if (!this.hoveredLineageIds.length) {
                return false;
            }

            return !this.hoveredLineageIds.includes(layoutId);
        },
        isCardHighlighted(layoutId) {
            if (!this.hoveredLineageIds.length) {
                return false;
            }

            return this.hoveredLineageIds.includes(layoutId);
        },
        applyZoom(nextZoom, pointerX, pointerY) {
            const clampedZoom = Math.max(this.MIN_ZOOM, Math.min(this.MAX_ZOOM, nextZoom));

            const worldX = (pointerX - this.pan.x) / this.zoom;
            const worldY = (pointerY - this.pan.y) / this.zoom;

            this.zoom = clampedZoom;
            this.pan.x = pointerX - worldX * this.zoom;
            this.pan.y = pointerY - worldY * this.zoom;
        },
        onWheel(event) {
            const viewport = this.$refs.viewport;

            if (!viewport) {
                return;
            }

            // Match map-like behavior when enabled: only zoom on Ctrl/Cmd + wheel.
            if (this.cooperativeGestures && !event.ctrlKey && !event.metaKey) {
                return;
            }

            event.preventDefault();

            const zoomFactor = event.deltaY > 0 ? 0.92 : 1.08;
            const nextZoom = this.zoom * zoomFactor;

            const rect = viewport.getBoundingClientRect();
            const pointerX = event.clientX - rect.left;
            const pointerY = event.clientY - rect.top;

            this.applyZoom(nextZoom, pointerX, pointerY);
        },
        getTouchPoint(touch, rect) {
            return {
                x: touch.clientX - rect.left,
                y: touch.clientY - rect.top
            };
        },
        getTouchCenter(touchA, touchB, rect) {
            const pointA = this.getTouchPoint(touchA, rect);
            const pointB = this.getTouchPoint(touchB, rect);

            return {
                x: (pointA.x + pointB.x) / 2,
                y: (pointA.y + pointB.y) / 2
            };
        },
        getTouchDistance(touchA, touchB) {
            const deltaX = touchA.clientX - touchB.clientX;
            const deltaY = touchA.clientY - touchB.clientY;

            return Math.hypot(deltaX, deltaY);
        },
        onTouchStart(event) {
            const viewport = this.$refs.viewport;

            if (!viewport) {
                return;
            }

            const { touches } = event;
            const rect = viewport.getBoundingClientRect();

            if (touches.length >= 2) {
                this.isPanning = false;
                this.isPinching = true;
                this.lastPinchDistance = this.getTouchDistance(touches[0], touches[1]);
                this.lastTouchCenter = this.getTouchCenter(touches[0], touches[1], rect);
                return;
            }

            if (touches.length === 1) {
                this.isPinching = false;
                this.lastPinchDistance = 0;
                this.lastTouchCenter = null;

                const point = this.getTouchPoint(touches[0], rect);

                this.isPanning = true;
                this.panOrigin = {
                    x: point.x - this.pan.x,
                    y: point.y - this.pan.y
                };
            }
        },
        onTouchMove(event) {
            const viewport = this.$refs.viewport;

            if (!viewport) {
                return;
            }

            const { touches } = event;
            const rect = viewport.getBoundingClientRect();

            if (touches.length >= 2) {
                event.preventDefault();

                const center = this.getTouchCenter(touches[0], touches[1], rect);
                const distance = this.getTouchDistance(touches[0], touches[1]);

                if (!this.isPinching) {
                    this.isPinching = true;
                    this.isPanning = false;
                    this.lastPinchDistance = distance;
                    this.lastTouchCenter = center;
                    return;
                }

                if (this.lastTouchCenter) {
                    this.pan.x += center.x - this.lastTouchCenter.x;
                    this.pan.y += center.y - this.lastTouchCenter.y;
                }

                if (this.lastPinchDistance > 0 && distance > 0) {
                    const nextZoom = this.zoom * (distance / this.lastPinchDistance);
                    this.applyZoom(nextZoom, center.x, center.y);
                }

                this.lastPinchDistance = distance;
                this.lastTouchCenter = center;
                return;
            }

            if (touches.length === 1 && this.isPanning && !this.isPinching) {
                event.preventDefault();

                const point = this.getTouchPoint(touches[0], rect);
                this.pan.x = point.x - this.panOrigin.x;
                this.pan.y = point.y - this.panOrigin.y;
            }
        },
        onTouchEnd(event) {
            const viewport = this.$refs.viewport;

            if (!viewport) {
                return;
            }

            const { touches } = event;
            const rect = viewport.getBoundingClientRect();

            if (touches.length >= 2) {
                this.isPinching = true;
                this.isPanning = false;
                this.lastPinchDistance = this.getTouchDistance(touches[0], touches[1]);
                this.lastTouchCenter = this.getTouchCenter(touches[0], touches[1], rect);
                return;
            }

            if (touches.length === 1) {
                const point = this.getTouchPoint(touches[0], rect);

                this.isPinching = false;
                this.lastPinchDistance = 0;
                this.lastTouchCenter = null;
                this.isPanning = true;
                this.panOrigin = {
                    x: point.x - this.pan.x,
                    y: point.y - this.pan.y
                };
                return;
            }

            this.isPanning = false;
            this.isPinching = false;
            this.lastPinchDistance = 0;
            this.lastTouchCenter = null;
        },
        zoomIn() {
            const viewport = this.$refs.viewport;

            if (!viewport) {
                return;
            }

            const rect = viewport.getBoundingClientRect();
            this.applyZoom(this.zoom * 1.12, rect.width / 2, rect.height / 2);
        },
        zoomOut() {
            const viewport = this.$refs.viewport;

            if (!viewport) {
                return;
            }

            const rect = viewport.getBoundingClientRect();
            this.applyZoom(this.zoom * 0.88, rect.width / 2, rect.height / 2);
        },
        panBy(deltaX, deltaY) {
            this.pan.x += deltaX;
            this.pan.y += deltaY;
        },
        emitLayoutChange() {
            this.$emit('layout-change', {
                width: this.layout.width,
                height: this.layout.height,
                contentBounds: this.layout.contentBounds,
                nodeCount: this.layout.nodes.length
            });
        },
        fitSceneToViewport() {
            const viewport = this.$refs.viewport;

            if (!viewport || !this.layout.width || !this.layout.height) {
                return;
            }

            const rect = viewport.getBoundingClientRect();
            const availableWidth = Math.max(rect.width - this.FIT_PADDING * 2, 1);
            const availableHeight = Math.max(rect.height - this.FIT_PADDING * 2, 1);
            const fitBounds = this.fitToContent && this.layout.contentBounds.width && this.layout.contentBounds.height
                ? this.layout.contentBounds
                : {
                    x: 0,
                    y: 0,
                    width: this.layout.width,
                    height: this.layout.height
                };
            const fitZoomX = availableWidth / fitBounds.width;
            const fitZoomY = availableHeight / fitBounds.height;
            const shouldFillVertically = this.isDescendantTree && this.fitToContent;
            const shouldKeepOriginReadable = this.includeOriginSiblings && !this.isDescendantTree;
            const minOriginReadableZoom = window.innerWidth <= 768 ? 0.42 : 0.56;
            const baseFittedZoom = shouldFillVertically
                ? Math.min(fitZoomY, this.maxFitZoom, this.MAX_ZOOM)
                : Math.min(fitZoomX, fitZoomY, this.maxFitZoom, this.MAX_ZOOM);
            const fittedZoom = shouldFillVertically
                ? baseFittedZoom * this.DESCENDANT_INITIAL_ZOOM_FACTOR
                : baseFittedZoom;
            const minFitZoom = this.isDescendantTree && this.fitToContent
                ? this.DESCENDANT_MIN_READABLE_FIT_ZOOM
                : shouldKeepOriginReadable
                    ? minOriginReadableZoom
                : this.MIN_ZOOM;

            this.zoom = Math.max(minFitZoom, fittedZoom);
            const rootNode = shouldFillVertically
                ? this.layout.nodes.find(node => node.depth === 0)
                : null;
            const selfNode = this.layout.nodes.find(node => node.personID === this.personId && node.depth === 0);
            const horizontalAnchorNode = selfNode || rootNode;

            this.pan = {
                x: horizontalAnchorNode
                    ? rect.width / 2 - horizontalAnchorNode.xCenter * this.zoom
                    : (rect.width - fitBounds.width * this.zoom) / 2 - fitBounds.x * this.zoom,
                y: (rect.height - fitBounds.height * this.zoom) / 2 - fitBounds.y * this.zoom
            };
        },
        fitToView() {
            this.fitSceneToViewport();
        },
        resetView() {
            this.fitSceneToViewport();
            this.clearHoveredLineage();
        },
        getRootPersonDisplayName() {
            if (!this.rootPerson) {
                return this.$t('treeViews.unknown');
            }

            return [this.rootPerson.firstname, this.rootPerson.lastname]
                .filter(Boolean)
                .join(' ')
                .trim() || this.$t('treeViews.unknown');
        },
        buildExportFilename() {
            const rawName = `${this.isDescendantTree ? 'nakomelingen' : 'voorouders'}-${this.getRootPersonDisplayName()}`;
            const slug = rawName
                .normalize('NFD')
                .replace(/[\u0300-\u036f]/g, '')
                .replace(/[^a-zA-Z0-9]+/g, '-')
                .replace(/^-+|-+$/g, '')
                .toLowerCase();

            return `${slug || (this.isDescendantTree ? 'nakomelingenboom' : 'voorouderboom')}.png`;
        },
        getExportScale(width, height) {
            const preferredScale = Math.max(2, window.devicePixelRatio || 1);
            const maxCanvasDimension = 30000;
            const maxCanvasArea = 160000000;
            const maxDimensionScale = maxCanvasDimension / Math.max(width, height, 1);
            const maxAreaScale = Math.sqrt(maxCanvasArea / Math.max(width * height, 1));

            return Math.max(0.5, Math.min(preferredScale, maxDimensionScale, maxAreaScale));
        },
        async downloadTreeImage() {
            const sceneEl = this.$refs.scene;

            if (!sceneEl || !this.layout.width || !this.layout.height) {
                return;
            }

            const wrapperWidth = this.layout.width + 80;
            const wrapperHeight = this.layout.height + 240;
            const exportBackgroundColor =
                getComputedStyle(document.documentElement).getPropertyValue('--brand-bg-4').trim()
                || getComputedStyle(document.body).backgroundColor
                || '#111a18';
            const wrapper = document.createElement('div');
            const title = document.createElement('h2');
            const watermark = document.createElement('div');
            const sceneClone = sceneEl.cloneNode(true);

            try {
                this.clearHoveredLineage();
                await this.$nextTick();

                if (document.fonts?.ready) {
                    await document.fonts.ready;
                }

                wrapper.style.position = 'fixed';
                wrapper.style.left = '-100000px';
                wrapper.style.top = '0';
                wrapper.style.padding = '40px';
                wrapper.style.backgroundColor = exportBackgroundColor;
                wrapper.style.display = 'flex';
                wrapper.style.flexDirection = 'column';
                wrapper.style.alignItems = 'center';
                wrapper.style.width = 'fit-content';
                wrapper.style.pointerEvents = 'none';

                title.style.textAlign = 'center';
                title.style.fontSize = '18px';
                title.style.fontWeight = 'bold';
                title.style.color = '#fff';
                title.style.margin = '0 0 30px';
                title.style.lineHeight = '1.4';
                title.textContent = `${this.$t(this.isDescendantTree ? 'treeViews.descendantsLink' : 'treeViews.ancestorsLink')}: ${this.getRootPersonDisplayName()}`;

                sceneClone.style.position = 'relative';
                sceneClone.style.top = '0';
                sceneClone.style.left = '0';
                sceneClone.style.width = `${this.layout.width}px`;
                sceneClone.style.height = `${this.layout.height}px`;
                sceneClone.style.transform = 'none';
                sceneClone.style.transformOrigin = '0 0';

                watermark.style.textAlign = 'center';
                watermark.style.marginTop = '20px';
                watermark.style.fontSize = '12px';
                watermark.style.color = '#aaa';
                watermark.style.opacity = '0.6';
                watermark.style.lineHeight = '1.5';
                watermark.innerHTML = `
                    Familiegeschiedenis onderzoek en samenstelling:<br>
                    © Jeffrey Thümann 2026<br>
                    sejarah-thumann.online
                `;

                wrapper.appendChild(title);
                wrapper.appendChild(sceneClone);
                wrapper.appendChild(watermark);
                document.body.appendChild(wrapper);

                const canvas = await html2canvas(wrapper, {
                    backgroundColor: exportBackgroundColor,
                    scale: this.getExportScale(wrapperWidth, wrapperHeight),
                    windowWidth: Math.ceil(wrapperWidth),
                    windowHeight: Math.ceil(wrapperHeight),
                    useCORS: true,
                    logging: false,
                    onclone: (clonedDoc) => {
                        const textSelectors = [
                            '.PersonCard_name',
                            '.PersonCard_years',
                        ];
                        clonedDoc.querySelectorAll(textSelectors.join(',')).forEach((el) => {
                            el.style.overflow = 'visible';
                            el.style.textOverflow = 'clip';
                            el.style.lineHeight = '1.3';
                            el.style.paddingBottom = '0.15em';
                            el.style.transform = 'translateY(-8px)';
                        });

                        clonedDoc.querySelectorAll('.PersonCard_years img').forEach((el) => {
                            el.style.transform = 'translateY(8px)';
                        });

                        clonedDoc.querySelectorAll('.PersonCard_text_wrapper').forEach((el) => {
                            el.style.overflow = 'visible';
                        });
                    },
                });

                const link = document.createElement('a');
                link.href = canvas.toDataURL('image/png');
                link.download = this.buildExportFilename();
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            } catch (error) {
                console.error(`Fout bij exporteren van ${this.isDescendantTree ? 'nakomelingenboom' : 'voorouderboom'}:`, error);
            } finally {
                wrapper.remove();
            }
        },
        async fetchTree() {
            if (!this.personId) {
                return;
            }

            const requestId = this.fetchRequestId + 1;
            this.fetchRequestId = requestId;
            this.loading = true;
            this.error = '';

            try {
                if (this.isDescendantTree) {
                    this.descendantAbortController?.abort();
                    this.descendantAbortController = null;

                    if (
                        this.visibleDescendantGenerations === 1
                        && this.initialRootPerson
                        && this.familyGroups.length > 0
                    ) {
                        this.rootPerson = {
                            ...this.initialRootPerson,
                            descendantFamilies: [],
                            descendants: []
                        };
                        this.originSiblingGroups = [];
                        this.clearHoveredLineage();
                        return;
                    }

                    const controller = new AbortController();
                    this.descendantAbortController = controller;
                    const data = await getDescendants(
                        this.personId,
                        this.descendantFetchGenerations,
                        { signal: controller.signal }
                    );

                    if (requestId !== this.fetchRequestId) {
                        return;
                    }

                    this.rootPerson = this.limitDescendantDepth(data, this.visibleDescendantGenerations);
                    this.originSiblingGroups = [];
                } else {
                    const [data, siblingsData] = await Promise.all([
                        getAncestors(this.personId, this.selectedGenerations),
                        this.fetchOriginSiblingsData(this.personId)
                    ]);

                    if (requestId !== this.fetchRequestId) {
                        return;
                    }

                    this.rootPerson = data;
                    this.originSiblingGroups = this.buildOriginSiblingGroups(data, siblingsData);
                }
                this.clearHoveredLineage();
            } catch (error) {
                if (requestId !== this.fetchRequestId) {
                    return;
                }

                if (error?.name === 'AbortError' || error?.code === 'ERR_CANCELED') {
                    return;
                }

                console.error(`Error loading ${this.treeMode} person-card tree:`, error);
                this.error = this.loadErrorLabel;
                this.rootPerson = null;
                this.originSiblingGroups = [];
                this.clearHoveredLineage();
            } finally {
                if (requestId === this.fetchRequestId) {
                    this.descendantAbortController = null;
                    this.loading = false;

                    if (this.rootPerson && !this.error) {
                        this.$nextTick(() => {
                            this.emitLayoutChange();

                            this.$nextTick(() => {
                                this.fitSceneToViewport();
                            });
                        });
                    }
                }
            }
        },
        fetchAncestorTree() {
            return this.fetchTree();
        }
    }
};
</script>

<style scoped>
.ancestor-person-cards-view {
    min-height: 100vh;
    padding: 132px 24px 24px;
    background: url('@/assets/noise128x128.png'), var(--brand-bg-4);
    background-repeat: repeat;
    background-size: auto;
}

.ancestor-person-cards-view__canvas {
    overflow: hidden;
    border-radius: 14px;
    min-height: calc(100vh - 180px);
    background: rgba(0, 0, 0, 0.2);
    position: relative;
    cursor: grab;
    user-select: none;
    touch-action: none;
}

.ancestor-person-cards-view__canvas:active {
    cursor: grabbing;
}

.ancestor-person-cards-view__hint {
    position: absolute;
    top: 10px;
    right: 10px;
    font-size: 12px;
    font-weight: 600;
    color: rgba(255, 255, 255, 0.8);
    background: rgba(0, 0, 0, 0.35);
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: var(--border-radius-md);
    padding: 6px 10px;
    z-index: 2;
}

.ancestor-person-cards-view__controls {
    position: absolute;
    right: 10px;
    bottom: 10px;
    /* display: flex; */
    display: none;
    /* Hidden for now, as the buttons are not fully functional yet */
    align-items: center;
    gap: 6px;
    flex-wrap: wrap;
    max-width: 280px;
    background: rgba(0, 0, 0, 0.42);
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 10px;
    padding: 8px;
    z-index: 3;
}

.ancestor-person-cards-view__control-btn {
    width: 30px;
    height: 30px;
    border: 1px solid rgba(255, 255, 255, 0.25);
    border-radius: var(--border-radius-md);
    background: rgba(255, 255, 255, 0.12);
    color: #fff;
    font-size: 14px;
    font-weight: 700;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    line-height: 1;
}

.ancestor-person-cards-view__control-btn:hover {
    background: rgba(255, 255, 255, 0.22);
}

.ancestor-person-cards-view__control-btn--wide {
    width: auto;
    padding: 0 10px;
    font-size: 12px;
}

.ancestor-person-cards-view__pan-pad {
    display: grid;
    grid-template-columns: repeat(4, 30px);
    gap: 6px;
}

.ancestor-canvas-scene {
    position: absolute;
    top: 0;
    left: 0;
    transform-origin: 0 0;
}

.ancestor-canvas-scene__lines {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 1;
    pointer-events: none;
}

.ancestor-canvas-scene__line {
    fill: none;
    stroke: rgba(255, 255, 255, 0.72);
    stroke-width: 2;
    vector-effect: non-scaling-stroke;
}

.ancestor-canvas-scene__line--family-parent,
.ancestor-canvas-scene__line--family-rail,
.ancestor-canvas-scene__line--family-child {
    stroke: var(--family-accent, rgba(255, 255, 255, 0.72));
    stroke-width: 2.35;
}

.ancestor-canvas-scene__line--family-parent {
    stroke-opacity: 0.95;
}

.ancestor-canvas-scene__line--family-rail,
.ancestor-canvas-scene__line--family-child {
    stroke-opacity: 0.86;
}

.ancestor-canvas-scene__family-band {
    position: absolute;
    border: 1px solid var(--family-border, color-mix(in srgb, var(--family-accent, #fff) 44%, transparent));
    border-top-width: 3px;
    border-radius: 12px;
    background:
        linear-gradient(180deg,
            var(--family-fill-strong, color-mix(in srgb, var(--family-accent, #fff) 16%, transparent)),
            var(--family-fill-soft, color-mix(in srgb, var(--family-accent, #fff) 7%, transparent)));
    box-shadow:
        inset 0 1px 0 rgba(255, 255, 255, 0.08),
        var(--family-shadow, 0 16px 34px rgba(0, 0, 0, 0.18));
    pointer-events: none;
    z-index: 0;
}

.ancestor-canvas-scene__family-band-label {
    position: absolute;
    top: 10px;
    left: 14px;
    max-width: calc(100% - 28px);
    color: var(--family-label-color, color-mix(in srgb, var(--family-accent, #fff) 65%, #ffffff));
    font-size: 13px;
    font-weight: 800;
    letter-spacing: 0;
    line-height: 1.2;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.55);
    opacity: 0.92;
}

.ancestor-canvas-scene__card {
    position: absolute;
    height: 112px;
    opacity: 1;
    transition: opacity 0.18s ease;
    z-index: 2;
}

.ancestor-canvas-scene__card:hover,
.ancestor-canvas-scene__card:focus-within {
    z-index: 3;
}

.ancestor-canvas-scene__card--dimmed {
    opacity: 0.25;
}

.ancestor-canvas-scene__card--lineage {
    opacity: 1;
}

.ancestor-canvas-scene__card :deep(.PersonCard) {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    box-sizing: border-box;
    transition: width 0.2s cubic-bezier(.49, 1.42, .25, 1.53);
}

.ancestor-canvas-scene__card :deep(.PersonCard:hover),
.ancestor-canvas-scene__card :deep(.PersonCard:focus-visible) {
    width: var(--tree-card-expanded-width, 100%);
    min-width: 100%;
}

.ancestor-canvas-scene__card :deep(.PersonCard_name),
.ancestor-canvas-scene__card :deep(.PersonCard_years) {
    width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.ancestor-canvas-scene__card :deep(.PersonCard_text_wrapper) {
    width: 100%;
    min-width: 0;
}

.ancestor-canvas-scene__card :deep(.PersonCard:hover .PersonCard_text_wrapper),
.ancestor-canvas-scene__card :deep(.PersonCard:focus-visible .PersonCard_text_wrapper),
.ancestor-canvas-scene__card :deep(.PersonCard:hover .PersonCard_name),
.ancestor-canvas-scene__card :deep(.PersonCard:focus-visible .PersonCard_name),
.ancestor-canvas-scene__card :deep(.PersonCard:hover .PersonCard_years),
.ancestor-canvas-scene__card :deep(.PersonCard:focus-visible .PersonCard_years) {
    width: auto;
    overflow: visible;
    text-overflow: clip;
}

.ancestor-person-cards-view__status {
    color: var(--brand-text-light);
    font-weight: 600;
}

.ancestor-person-cards-view__status--error {
    color: #ffb4b4;
}

@media (max-width: 768px) {
    .ancestor-person-cards-view {
        padding: 120px 12px 12px;
    }

    .ancestor-person-cards-view__canvas {
        min-height: calc(100vh - 148px);
    }

    .ancestor-person-cards-view__hint {
        left: 10px;
        right: auto;
    }

    .ancestor-person-cards-view__controls {
        left: 10px;
        right: auto;
        max-width: calc(100% - 20px);
    }
}
</style>
